import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle, Download, Building2, Search, Sparkles, Eye, Zap } from "lucide-react";
import { toast } from "sonner";
import { leadsApi } from "@/lib/leadsApi";
import { useI18n } from "@/lib/i18n";
import { FRENCH_DEPARTMENTS, NAF_SHORTCUTS } from "@/lib/frenchDepartments";

const TEMPLATE_CSV = `first_name,last_name,title,company_name,company_domain,industry,company_size,country,city,email,phone,linkedin
Ada,Lovelace,CTO,Analytical Engine,analytical-engine.com,SaaS,11-50,United Kingdom,London,ada@analytical-engine.com,+44 20 1234 5678,https://linkedin.com/in/adalovelace
Alan,Turing,CEO,Enigma Systems,enigma.systems,Cybersecurity,51-200,United Kingdom,Cambridge,,,https://linkedin.com/in/alanturing`;

export default function ImportCsv() {
    const { t } = useI18n();
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [drag, setDrag] = useState(false);

    async function handleFile(file) {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".csv")) {
            toast.error("Only .csv files supported");
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            const res = await leadsApi.importCsv(file);
            setResult(res);
            toast.success(`${res.inserted} leads imported`);
        } catch (_e) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    }

    function download() {
        const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "boxeb2b-import-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{t.find.importCsv.title}</h1>
                <p className="mt-1 text-sm text-zinc-500 max-w-2xl">{t.find.importCsv.subtitle}</p>
                <p className="mt-1 text-xs text-zinc-600">{t.find.importCsv.optional}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Guyane one-click starter */}
                    <GuyaneStarter />

                    {/* Gouv.fr real registry importer */}
                    <GouvImporter />

                    <div
                        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
                        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                        onDragLeave={() => setDrag(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDrag(false);
                            const f = e.dataTransfer.files?.[0];
                            handleFile(f);
                        }}
                        data-testid="import-dropzone"
                        className={`relative rounded-2xl border-2 border-dashed transition p-12 text-center overflow-hidden ${
                            drag ? "border-brand-indigo bg-brand-indigo/10" : "border-white/15 bg-brand-surface"
                        }`}
                    >
                        <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center mb-4">
                            <Upload size={22} className="text-white" />
                        </div>
                        <div className="text-lg font-display font-bold">{t.find.importCsv.drop}</div>
                        <div className="text-sm text-zinc-500 mt-1">{t.find.importCsv.or}</div>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            data-testid="import-file-input"
                        />
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            data-testid="import-pick"
                            className="mt-4 btn-cta text-white font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {uploading ? <><Loader2 size={14} className="animate-spin" /> {t.find.importCsv.uploading}</> : <><FileText size={14} /> {t.find.importCsv.pick}</>}
                        </button>
                    </div>

                    {result && (
                        <div className="mt-4 rounded-2xl p-5 bg-brand-surface border border-white/8" data-testid="import-result">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle2 size={18} className="text-emerald-400" />
                                <div className="text-lg font-display font-bold text-emerald-300">{t.find.importCsv.imported(result.inserted)}</div>
                            </div>
                            {result.skipped > 0 && (
                                <div className="flex items-center gap-2 text-sm text-amber-300">
                                    <AlertTriangle size={14} /> {t.find.importCsv.skipped(result.skipped)}
                                </div>
                            )}
                            {result.errors?.length > 0 && (
                                <div className="mt-3">
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1">{t.find.importCsv.errors}</div>
                                    <ul className="text-xs text-rose-300 font-mono space-y-0.5 max-h-32 overflow-y-auto">
                                        {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                                    </ul>
                                </div>
                            )}
                            <Link
                                to="/app/find/leads"
                                data-testid="import-goto-leads"
                                className="mt-4 inline-block btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full"
                            >
                                {t.find.importCsv.gotoLeads}
                            </Link>
                        </div>
                    )}
                </div>

                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-sm font-semibold mb-2">Template</div>
                    <p className="text-xs text-zinc-500 mb-4">Download a CSV example with required and optional columns.</p>
                    <button
                        type="button"
                        onClick={download}
                        data-testid="import-download-template"
                        className="w-full text-center border border-white/12 hover:border-white/25 hover:bg-white/5 text-white text-sm px-4 py-2 rounded-lg inline-flex items-center justify-center gap-2"
                    >
                        <Download size={14} /> {t.find.importCsv.downloadTemplate}
                    </button>
                    <div className="mt-6 text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Required</div>
                    <div className="flex flex-wrap gap-1">
                        {["first_name", "last_name", "title", "company_name"].map((c) => (
                            <span key={c} className="text-[11px] font-mono px-2 py-0.5 rounded bg-brand-indigo/15 border border-brand-indigo/30 text-brand-cyan">{c}</span>
                        ))}
                    </div>
                    <div className="mt-4 text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Optional</div>
                    <div className="flex flex-wrap gap-1">
                        {["company_domain", "industry", "company_size", "country", "city", "email", "phone", "linkedin"].map((c) => (
                            <span key={c} className="text-[11px] font-mono px-2 py-0.5 rounded bg-white/[0.04] border border-white/10 text-zinc-300">{c}</span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------- Gouv.fr importer ----------

function GuyaneStarter() {
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    async function launch() {
        if (running) return;
        setRunning(true);
        setResult(null);
        const toastId = toast.loading("Scanning ~35 Guyane sectors on data.gouv.fr…");
        try {
            const res = await leadsApi.seedGuyane(30);
            setResult(res);
            toast.success(`${res.leads_inserted} leads Guyane importés (${res.sectors_scanned} secteurs, ${res.companies_fetched} sociétés)`, { id: toastId });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Seed failed", { id: toastId });
        } finally {
            setRunning(false);
        }
    }

    return (
        <div
            className="relative rounded-2xl p-6 border border-emerald-500/25 overflow-hidden"
            style={{ background: "radial-gradient(ellipse at 0% 0%, rgba(16,185,129,0.18), transparent 55%), radial-gradient(ellipse at 100% 100%, rgba(6,182,212,0.12), transparent 55%), #1A1A2E" }}
            data-testid="guyane-starter-card"
        >
            <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />
            <div className="relative flex items-start gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-brand-cyan flex items-center justify-center shrink-0">
                    <span className="text-2xl">🇬🇫</span>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h2 className="font-display font-bold text-xl tracking-tight">Starter Guyane (973)</h2>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">1 click · gratuit</span>
                    </div>
                    <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                        Importe en un clic <span className="text-brand-cyan font-semibold">~600-900 leads réels</span> — vraies PME dont le <b>siège est en Guyane</b>,
                        réparties sur ~35 secteurs clés : BTP, commerce, restauration, tourisme, transport, agriculture, pêche, services.
                    </p>
                    <p className="mt-1 text-[11px] text-zinc-500 font-mono">
                        Source : recherche-entreprises.api.gouv.fr (INSEE officiel · RGPD-clean)
                    </p>

                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={launch}
                            disabled={running}
                            data-testid="guyane-seed-btn"
                            className="btn-cta text-white font-semibold text-sm px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {running ? <><Loader2 size={14} className="animate-spin" /> Import en cours (30-60s)…</> : <><Zap size={14} /> Lancer le seed Guyane</>}
                        </button>
                        {running && (
                            <span className="text-[11px] text-zinc-500 font-mono">
                                On scanne data.gouv.fr, tu peux naviguer ailleurs.
                            </span>
                        )}
                    </div>

                    {result && (
                        <div className="mt-4 rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/25" data-testid="guyane-seed-result">
                            <div className="flex items-center gap-2 text-emerald-300 text-sm">
                                <CheckCircle2 size={16} />
                                <div>
                                    <span className="font-semibold">{result.leads_inserted}</span> leads Guyane insérés ·{" "}
                                    <span className="text-zinc-400">{result.sectors_scanned} secteurs scannés · {result.companies_fetched} sociétés récupérées</span>
                                </div>
                            </div>
                            {result.errors?.length > 0 && (
                                <div className="mt-2 text-xs text-amber-300 font-mono">
                                    ⚠ {result.errors.length} secteur(s) en erreur (retry possible)
                                </div>
                            )}
                            <Link
                                to="/app/find/leads"
                                className="mt-3 inline-block btn-cta text-white font-semibold text-xs px-4 py-1.5 rounded-full"
                            >
                                Voir mes leads →
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------- Gouv.fr importer ----------

function GouvImporter() {
    const [department, setDepartment] = useState("973");
    const [nafCode, setNafCode] = useState("88.91A");
    const [query, setQuery] = useState("");
    const [limit, setLimit] = useState(50);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    async function doPreview() {
        setPreviewing(true);
        setResult(null);
        try {
            const p = await leadsApi.importGouvPreview({
                department: department || null,
                naf_code: nafCode || null,
                query: query || null,
                limit: Math.min(limit, 100),
            });
            setPreview(p);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setPreviewing(false);
        }
    }

    async function doImport() {
        if (!preview) return;
        setImporting(true);
        try {
            const res = await leadsApi.importGouv({
                department: department || null,
                naf_code: nafCode || null,
                query: query || null,
                limit,
            });
            setResult(res);
            toast.success(`${res.leads_inserted} leads imported from data.gouv.fr`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally {
            setImporting(false);
        }
    }

    return (
        <div
            className="relative rounded-2xl p-6 border border-white/8 overflow-hidden"
            style={{ background: "radial-gradient(ellipse at 0% 0%, rgba(6,182,212,0.15), transparent 55%), radial-gradient(ellipse at 100% 100%, rgba(99,102,241,0.12), transparent 55%), #1A1A2E" }}
        >
            <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />
            <div className="relative">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-indigo flex items-center justify-center shrink-0">
                        <Building2 size={16} className="text-white" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="font-display font-bold text-xl tracking-tight">Import from data.gouv.fr</h2>
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full badge-cyan">RGPD · Official source</span>
                        </div>
                        <p className="text-sm text-zinc-400 mt-1">
                            Pull real French companies from the official INSEE / annuaire-entreprises registry.
                            Filter by department, NAF activity code, or free-text query. Dirigeants (Président, Gérant, etc.) are imported as leads.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-1">
                        <label className="text-xs text-zinc-500 mb-1 block">Department</label>
                        <select
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            data-testid="gouv-department"
                            className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-cyan"
                        >
                            {FRENCH_DEPARTMENTS.map(([code, label]) => (
                                <option key={code || "all"} value={code}>{label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-xs text-zinc-500 mb-1 block">NAF activity code</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={nafCode}
                                onChange={(e) => setNafCode(e.target.value.toUpperCase())}
                                placeholder="88.91A"
                                data-testid="gouv-naf"
                                className="flex-1 px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-cyan font-mono"
                            />
                            <select
                                value=""
                                onChange={(e) => e.target.value && setNafCode(e.target.value)}
                                data-testid="gouv-naf-preset"
                                className="px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-cyan max-w-[180px]"
                            >
                                <option value="">Presets…</option>
                                {NAF_SHORTCUTS.map((n) => (
                                    <option key={n.code} value={n.code}>{n.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500 mb-1 block">Limit</label>
                        <input
                            type="number"
                            min={1}
                            max={500}
                            value={limit}
                            onChange={(e) => setLimit(parseInt(e.target.value, 10) || 50)}
                            data-testid="gouv-limit"
                            className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-cyan font-mono"
                        />
                    </div>
                </div>

                <div className="mt-3">
                    <label className="text-xs text-zinc-500 mb-1 block">Optional free-text query</label>
                    <div className="relative">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="e.g. crèche montessori, boulangerie bio…"
                            data-testid="gouv-query"
                            className="w-full pl-8 pr-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-cyan"
                        />
                    </div>
                </div>

                <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={doPreview}
                        disabled={previewing}
                        data-testid="gouv-preview"
                        className="text-white font-medium text-sm px-4 py-2 rounded-full border border-white/12 hover:border-brand-cyan/50 hover:bg-brand-cyan/5 transition inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {previewing ? <><Loader2 size={13} className="animate-spin" /> Fetching…</> : <><Eye size={13} /> Preview</>}
                    </button>
                    <button
                        type="button"
                        onClick={doImport}
                        disabled={!preview || importing}
                        data-testid="gouv-import"
                        className="btn-cta text-white font-semibold text-sm px-5 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-40"
                    >
                        {importing ? <><Loader2 size={13} className="animate-spin" /> Importing…</> : <><Sparkles size={13} /> Import all</>}
                    </button>
                    <a
                        href={`https://annuaire-entreprises.data.gouv.fr/departements/${department}/${nafCode}/1.html`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto text-xs text-zinc-500 hover:text-brand-cyan transition"
                    >
                        View source ↗
                    </a>
                </div>

                {preview && (
                    <div className="mt-5 rounded-xl bg-brand-void/60 border border-white/8 overflow-hidden" data-testid="gouv-preview-box">
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8">
                            <div className="text-sm text-white">
                                <span className="font-mono text-brand-cyan">{preview.fetched}</span> companies · <span className="font-mono text-brand-cyan">{preview.would_insert_leads}</span> leads to insert
                            </div>
                            <div className="text-[10px] font-mono text-zinc-500">Preview only · click Import to save</div>
                        </div>
                        <ul className="divide-y divide-white/5 max-h-72 overflow-y-auto">
                            {preview.sample.map((row, i) => (
                                <li key={i} className="px-4 py-2.5 text-sm">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-white font-medium truncate">{row.company_name}</span>
                                        <span className="text-zinc-500">·</span>
                                        <span className="text-zinc-300 truncate">{row.dirigeant}</span>
                                        <span className="text-zinc-500 text-xs">— {row.title}</span>
                                        <span className="flex-1" />
                                        <span className="font-mono text-[10px] text-zinc-500">{row.siren}</span>
                                        <span className="text-xs text-brand-cyan">{row.city}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {result && (
                    <div className="mt-5 rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/25" data-testid="gouv-result">
                        <div className="flex items-center gap-2 text-emerald-300">
                            <CheckCircle2 size={16} />
                            <div className="text-sm">
                                <span className="font-semibold">{result.leads_inserted}</span> leads imported ·{" "}
                                <span className="text-zinc-400">{result.companies_fetched} companies fetched · {result.skipped} skipped (already in DB)</span>
                            </div>
                        </div>
                        <Link
                            to="/app/find/leads"
                            className="mt-3 inline-block text-xs text-emerald-300 hover:text-white transition"
                        >
                            View leads →
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
